/**
 * Runner API istemcisi.
 *
 * Ince bir fetch sarmalayici: session token'i ekler, hata govdesini cozer.
 * Runner API'si gercek HTTP status kodlari kullanir (mevcut V2 yuzeyinden
 * farkli olarak), bu yuzden 401 ile 403 transport seviyesinde ayirt edilebilir.
 */

import { loadConfig } from "./config";

/** Sunucunun donduğu hata govdesi: { error, message } */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Token gecersiz veya suresi dolmus. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Token baska bir session'a ait. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Durum uygun degil: tamamlanmis session, eksik item, sirasi gelmemis item. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

interface RequestOptions {
  method?: "GET" | "POST";
  token: string;
  body?: unknown;
  signal?: AbortSignal;
}

/** Zayıf bağlantı: ağ hatası ya da geçici sunucu hatasında bu aralıklarla yeniden denenir (26 Eyl 2026). */
export const RETRY_DELAYS_MS = [1000, 2000, 4000, 6000];
const GECICI = new Set([502, 503, 504]);

/** Tek denemenin en uzun süresi (8 Eki 2026). Eskiden sınır yoktu: mobil bağlantı "ne yanıt ne hata" durumunda
 * takılınca istek hiç bitmiyor, oynatıcıdaki ileri düğmesi kilitli kalıyordu. Süre dolan deneme ağ hatası gibi
 * yeniden denenir; denemeler bitince okunur bir hata fırlatılır (oynatıcı gösterir, düğme açılır). */
export const ISTEK_ZAMAN_ASIMI_MS = 10000;
export const ZAMAN_ASIMI_MESAJI = "Bağlantı yavaş, cevap gönderilemedi. Lütfen tekrar deneyin.";

/** fetch'i ağ hatasında, zaman aşımında ve 502/503/504'te yeniden dener. Cevap gönderiminde attemptId aynı
 * kaldığı için tekrar çift kayıt oluşturmaz (sunucu idempotent). Dışarıdan iptal (AbortSignal) yeniden denenmez. */
export async function fetchRetry(url: string, init: RequestInit, delays = RETRY_DELAYS_MS,
  zamanAsimiMs = ISTEK_ZAMAN_ASIMI_MS): Promise<Response> {
  const disSinyal = init.signal ?? undefined;
  for (let deneme = 0; ; deneme++) {
    const kontrol = new AbortController();
    let sureDoldu = false;
    const zamanlayici = setTimeout(() => { sureDoldu = true; kontrol.abort(); }, zamanAsimiMs);
    const disIptal = () => kontrol.abort();
    disSinyal?.addEventListener("abort", disIptal);
    try {
      const r = await fetch(url, { ...init, signal: kontrol.signal });
      if (!GECICI.has(r.status) || deneme >= delays.length) return r;
    } catch (e) {
      if (disSinyal?.aborted) throw e;                       // dışarıdan iptal: yeniden deneme
      if (!sureDoldu && (e as Error)?.name === "AbortError") throw e;
      if (deneme >= delays.length) throw sureDoldu ? new Error(ZAMAN_ASIMI_MESAJI) : e;
    } finally {
      clearTimeout(zamanlayici);
      disSinyal?.removeEventListener("abort", disIptal);
    }
    await new Promise((res) => setTimeout(res, delays[deneme]));
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions,
): Promise<T> {
  const { apiBaseUrl } = await loadConfig();
  const { method = "GET", token, body, signal } = options;

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetchRetry(`${apiBaseUrl}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = "unknown_error";
  let message = "Bir sorun oluştu. Lütfen tekrar deneyin.";

  try {
    const parsed = (await response.json()) as {
      error?: string;
      message?: string;
    };
    if (parsed.error) code = parsed.error;
    if (parsed.message) message = parsed.message;
  } catch {
    // Govde JSON degilse varsayilan mesajla devam et.
  }

  return new ApiError(response.status, code, message);
}
